import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  CircularProgress
} from '@mui/material';
import { sanitizeInput } from '../lib/validation';

interface TTXSchedule {
  ideal_time: string;
  ideal_duration: string;
  ideal_ccg: string;
  ideal_committee: string;
  ideal_location: string;
  actual_time: string;
  actual_duration: string;
  actual_ccg: string;
  actual_committee: string;
  actual_location: string;
  status: string;
  progress: string;
  start_at: string | null;
  stop_at: string | null;
  remarks: string;
  aremarks: string
}

const ADMIN_PIN = import.meta.env.VITE_REACT_APP_ADMIN_PIN;
const MAX_PIN_ATTEMPTS = 5;

function excelTimeToString(value: any) {
  if (typeof value === 'number') {
    // Excel time: 0.25 = 6:00 AM, 0.5 = 12:00 PM, etc.
    const totalSeconds = Math.round(value * 24 * 60 * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (seconds === 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return value;
}

export default function TTXImport() {
  // PIN State
  const [pin, setPin] = useState('');
  const [pinAttempts, setPinAttempts] = useState(0);
  const [isPinLocked, setIsPinLocked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // File Upload State
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<TTXSchedule[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  
  // Message State
  const [statusMessage, setStatusMessage] = useState<{
    type: 'success' | 'error' | 'info' | 'warning';
    text: string;
  } | null>(null);

  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const fetchUserRole = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('users')
          .select('user_role')
          .eq('id', user.id)
          .single();
        if (data) setUserRole(data.user_role);
      }
    };
    fetchUserRole();
  }, []);

  if (userRole && userRole !== 'god') {
    return <Alert severity="error">You do not have access to this page.</Alert>;
  }

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatusMessage(null);
    const sanitizedPin = sanitizeInput(pin);
    if (!/^[0-9]{6}$/.test(sanitizedPin)) {
      setStatusMessage({
        type: 'error',
        text: 'PIN must be a 6-digit number.'
      });
      setPin('');
      return;
    }
    if (sanitizedPin === ADMIN_PIN) {
      setIsAuthenticated(true);
      setPinAttempts(0);
    } else {
      const newAttempts = pinAttempts + 1;
      setPinAttempts(newAttempts);
      if (newAttempts >= MAX_PIN_ATTEMPTS) {
        setIsPinLocked(true);
        setStatusMessage({
          type: 'error',
          text: 'Too many failed attempts. Access locked.'
        });
      } else {
        setStatusMessage({
          type: 'error',
          text: `Incorrect PIN. ${MAX_PIN_ATTEMPTS - newAttempts} attempts remaining.`
        });
      }
    }
    setPin('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      setStatusMessage({
        type: 'error',
        text: 'No file selected.'
      });
      return;
    }
    if (!selectedFile.name.match(/\.(xlsx|csv)$/i)) {
      setStatusMessage({
        type: 'error',
        text: 'Invalid file type. Please select an .xlsx or .csv file.'
      });
      return;
    }
    setFile(selectedFile);
    setStatusMessage(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = event.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        // Read as array of arrays
        const rows: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

        // Find the header row (the one that starts with 'Time' and has at least 10 columns)
        const headerRowIdx = rows.findIndex(
          row => row[0]?.toString().toLowerCase() === 'time' && row.length >= 11
        );
        if (headerRowIdx === -1) {
          setStatusMessage({
            type: 'error',
            text: 'Could not find a valid header row. Please check your file format.'
          });
          setPreviewData([]);
          return;
        }

        const dataRows = rows.slice(headerRowIdx + 1);

        // Map data rows to objects
        const formattedData: TTXSchedule[] = dataRows
          .map(row => ({
            ideal_time: excelTimeToString(row[0]),
            ideal_duration: excelTimeToString(row[1]),
            ideal_ccg: row[2] || '',
            ideal_committee: row[3] || '',
            ideal_location: row[4] || '',
            actual_time: excelTimeToString(row[5]),
            actual_duration: excelTimeToString(row[6]),
            actual_ccg: row[7] || '',
            actual_committee: row[8] || '',
            actual_location: row[9] || '',
            status: 'Upcoming',
            progress: 'Yet to HOTO',
            start_at: null,
            stop_at: null,
            remarks: row[10] || '',
            aremarks: row[11] || '',
          }))
          .filter(row =>
            Object.values(row).some(val => val && val.toString().trim() !== '')
          );

        if (formattedData.length === 0) {
          setStatusMessage({
            type: 'error',
            text: 'No valid data found in the file. Please check your template and try again.'
          });
          setPreviewData([]);
          return;
        }

        setPreviewData(formattedData);
        setStatusMessage({
          type: 'success',
          text: `Successfully loaded ${formattedData.length} rows.`
        });
      } catch (error) {
        setStatusMessage({
          type: 'error',
          text: 'Failed to parse file. Please ensure it matches the required format.'
        });
        setPreviewData([]);
      }
    };

    reader.onerror = () => {
      setStatusMessage({
        type: 'error',
        text: 'Error reading file.'
      });
    };

    reader.readAsBinaryString(selectedFile);
  };

  const handleUpload = async () => {
    if (!previewData.length) return;

    setIsUploading(true);
    setStatusMessage(null);

    try {
      const { error } = await supabase
        .from('ttx_schedules')
        .upsert(previewData);

      if (error) {
        console.warn('TTX schedules table not found:', error);
        setStatusMessage({
          type: 'error',
          text: 'TTX schedules table not found. Please ensure the database is properly configured.'
        });
        return;
      }

      setStatusMessage({
        type: 'success',
        text: 'Successfully uploaded schedule to database!'
      });
      setPreviewData([]);
      setFile(null);
    } catch (error: any) {
      setStatusMessage({
        type: 'error',
        text: `Upload failed: ${error.message}`
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setPreviewData([]);
    setFile(null);
    setStatusMessage(null);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#1a237e', fontWeight: 'bold' }}>
        TTX Schedule Import
      </Typography>

      {statusMessage && (
        <Alert severity={statusMessage.type} sx={{ mb: 3 }}>
          {statusMessage.text}
        </Alert>
      )}

      {!isAuthenticated ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <form onSubmit={handlePinSubmit}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Admin PIN"
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                disabled={isPinLocked}
                inputProps={{ maxLength: 6 }}
                sx={{ width: 200 }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={isPinLocked}
              >
                Submit
              </Button>
            </Stack>
          </form>
        </Paper>
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack spacing={2}>
              <input
                type="file"
                accept=".xlsx,.csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button
                  variant="contained"
                  component="span"
                  disabled={isUploading}
                >
                  Select File
                </Button>
              </label>
              {file && (
                <Typography variant="body2" color="text.secondary">
                  Selected file: {file.name}
                </Typography>
              )}
            </Stack>
          </Paper>

          {previewData.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Preview ({previewData.length} rows)
              </Typography>
              <TableContainer sx={{ maxHeight: 400, mb: 2 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ bgcolor: '#e3f2fd' }}>
                        Ideal Timeline
                      </TableCell>
                      <TableCell colSpan={11} align="center" sx={{ bgcolor: '#fff3e0' }}>
                        Actual Timeline
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>CCG1 & CCG2</TableCell>
                      <TableCell>Committee</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>CCG1 & CCG2</TableCell>
                      <TableCell>Committee</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Start At</TableCell>
                      <TableCell>Stop At</TableCell>                      
                      <TableCell>Remarks</TableCell>
                      <TableCell>Additional Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.slice(0, 10).map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.ideal_time}</TableCell>
                        <TableCell>{row.ideal_duration}</TableCell>
                        <TableCell>{row.ideal_ccg}</TableCell>
                        <TableCell>{row.ideal_committee}</TableCell>
                        <TableCell>{row.ideal_location}</TableCell>
                        <TableCell>{row.actual_time}</TableCell>
                        <TableCell>{row.actual_duration}</TableCell>
                        <TableCell>{row.actual_ccg}</TableCell>
                        <TableCell>{row.actual_committee}</TableCell>
                        <TableCell>{row.actual_location}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>{row.progress}</TableCell>
                        <TableCell>{row.start_at}</TableCell>
                        <TableCell>{row.stop_at}</TableCell>
                        <TableCell>{row.remarks}</TableCell>
                        <TableCell>{row.aremarks}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpload}
                  disabled={isUploading}
                  startIcon={isUploading ? <CircularProgress size={20} /> : null}
                >
                  {isUploading ? 'Uploading...' : 'Upload to Database'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleReset}
                  disabled={isUploading}
                >
                  Reset
                </Button>
              </Stack>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
} 